/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板のレイアウト寸法・種類ごとの配色・横軸（日付/休日）生成ロジックといった描画定数を集約する
 * @graph-connects none
 */
import type { BlockKind, SandboxDay } from './types.js';

/** 候補プールの週セクション（インデックス・開始日ラベル・月曜の ISO 日付）。 */
export interface PoolWeek {
  /** 週インデックス（0 始まり）。 */
  index: number;
  /** その週の月曜の "M/D" ラベル。 */
  label: string;
  /**
   * その週の月曜の ISO 日付 (YYYY-MM-DD, ローカル暦)。
   * 候補案件の保存は相対添字 `weekIndex` ではなくこの絶対日付を基準にする
   * （週をまたぐと同じ添字が別の週を指してしまうため）。
   */
  startDate: string;
}

/** 左端メンバー列の幅（px）。 @graph-connects none */
export const MEMBER_COL_WIDTH = 168;
/** 1 日 1 列の幅（px）。 @graph-connects none */
export const DAY_WIDTH = 40;
/** メンバー 1 行の基本高さ（px。重なりが無い / 少ない行の高さ）。 @graph-connects none */
export const ROW_HEIGHT = 56;
/** 重なり並列時、1 レーンに最低確保する高さ（px。名称+pt を 1 行で読める高さ）。 @graph-connects none */
export const LANE_MIN_HEIGHT = 24;
/** 行内の上下パディング合計（px）。行高 = max(ROW_HEIGHT, laneCount*LANE_MIN_HEIGHT + ROW_PAD)。 @graph-connects none */
export const ROW_PAD = 6;
/** 日付ヘッダー行の高さ（px）。 @graph-connects none */
export const HEADER_HEIGHT = 48;
/**
 * 白板グリッドで最低限見えるようにするメンバー行数。ビューポート高に合わせて白板を
 * 潰すのではなく、まずこの行数ぶんの高さを確保する（下に編集履歴が入っても、
 * 何人ぶんかの予定が一目で読める状態を保つため）。
 * @graph-connects none
 */
export const MIN_VISIBLE_ROWS = 6;
/**
 * 白板グリッドの最小高さ（px）。日付ヘッダー + MIN_VISIBLE_ROWS 行ぶん。
 * 重なりで行が高くなっている場合は、見える行数はこれより少なくなる。
 * @graph-connects none
 */
export const BOARD_MIN_HEIGHT = HEADER_HEIGHT + MIN_VISIBLE_ROWS * ROW_HEIGHT;
/**
 * 白板グリッドの最大高さ。メンバーが多い白板はここで頭打ちにして内部縦スクロールへ切り替え、
 * 日付ヘッダー（sticky top）とメンバー列（sticky left）を固定したまま残す。
 * 上限を設けずページスクロールだけに任せると、白板が縦にスクロールしないため
 * sticky が発火せず、日付ヘッダーが流れて行と日付の対応が読めなくなる。
 * @graph-connects none
 */
export const BOARD_MAX_HEIGHT = '70vh';

/** 起点から過去に遡る日数（過去 1 週間）。 @graph-connects none */
export const PAST_DAYS = 7;
/** 起点から未来に伸ばす日数（未来 4 週間）。 @graph-connects none */
export const FUTURE_DAYS = 28;
/** 候補プールを分割する週セクション数。 @graph-connects none */
export const POOL_WEEK_COUNT = 4;
/** メンバー保有 pt を集計する「今日からの日数」窓（2 週間）。 @graph-connects none */
export const HOLDING_WINDOW_DAYS = 14;

/** 曜日ラベル（日〜土）。 @graph-connects none */
export const WEEKDAYS_JA: readonly string[] = ['日', '月', '火', '水', '木', '金', '土'];

/** 種類ごとの配色・ラベル i18n キー。 @graph-connects none */
export const KIND_STYLE: Record<BlockKind, { fill: string; text: string; i18nKey: string }> = {
  dev: { fill: '#F59E0B', text: '#1F2937', i18nKey: 'pjSandbox.kind.dev' },
  design: { fill: '#EC4899', text: '#FFFFFF', i18nKey: 'pjSandbox.kind.design' },
  bug: { fill: '#4B5563', text: '#FFFFFF', i18nKey: 'pjSandbox.kind.bug' },
};

/** 表示順を固定した種類リスト（作成フォームのトグル等で使用）。 @graph-connects none */
export const KIND_ORDER: readonly BlockKind[] = ['dev', 'design', 'bug'];

/** 週セクション見出しの配色（背景色と、その上で読める文字色の組）。 @graph-connects none */
export interface WeekHeadingColor {
  /** 見出しバッジの背景色。 @graph-connects none */
  bg: string;
  /** 背景色の上で十分なコントラストが出る文字色。 @graph-connects none */
  text: string;
}

/**
 * 候補プールの週セクション見出し色。青の同一色カードを濃淡で並べ、当週が最も濃く、先の週ほど淡くなる。
 * @graph-connects none
 */
export const WEEK_HEADING_COLORS: readonly [WeekHeadingColor, ...WeekHeadingColor[]] = [
  { bg: '#1D4ED8', text: '#FFFFFF' },
  { bg: '#2563EB', text: '#FFFFFF' },
  { bg: '#93C5FD', text: '#1F2937' },
  { bg: '#DBEAFE', text: '#1F2937' },
];

/**
 * 週インデックスに対応する見出し色を返す。色数を超えた週は最も淡い色で頭打ちにする。
 * @graph-connects none
 */
export function weekHeadingColor(weekIndex: number): WeekHeadingColor {
  const last = WEEK_HEADING_COLORS.length - 1;
  return WEEK_HEADING_COLORS[clamp(weekIndex, 0, last)] ?? WEEK_HEADING_COLORS[0];
}

/**
 * pt サイズから既定の跨ぎ日数を導く。2pt=1日 の粗い換算（1〜10 日にクランプ）。
 * @graph-connects none
 */
export function defaultSpanFromPt(sizePt: number): number {
  return Math.min(10, Math.max(1, Math.round(sizePt / 2)));
}

/**
 * 日付を h:m:s:ms=0 に正規化する（同日判定用）。
 * @graph-connects none
 */
function atMidnight(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Date をローカル暦の ISO 文字列 "YYYY-MM-DD" にする。
 * toISOString() は UTC 変換で日付がずれるため、ローカルの年月日から手組みする。
 * @graph-connects none
 */
function toLocalIso(d: Date): string {
  const y = d.getFullYear().toString();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * 過去 1 週間（PAST_DAYS）〜未来 4 週間（FUTURE_DAYS）の横軸を日単位で生成する。
 * 起点は「先週の月曜」に丸め、週の区切りを揃える。土日は休日として isWeekend を立てる。
 * @graph-connects none
 */
export function generateDays(base: Date = new Date()): SandboxDay[] {
  const today = atMidnight(base);

  // 起点 = 今日を含む週の月曜から 1 週間前（= 先週の月曜）。
  const start = atMidnight(base);
  const dow = start.getDay();
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  start.setDate(start.getDate() + diffToMonday - PAST_DAYS);

  // 週の区切りに揃えるため、総日数は PAST_DAYS ぶん先頭に足したうえで 7 の倍数へ切り上げる。
  const rawTotal = PAST_DAYS + FUTURE_DAYS + 1;
  const total = Math.ceil(rawTotal / 7) * 7;

  return Array.from({ length: total }, (_, index) => {
    const d = new Date(start);
    d.setDate(start.getDate() + index);
    const wd = d.getDay();
    const isFirstOfMonth = d.getDate() === 1;
    return {
      index,
      iso: toLocalIso(d),
      label: d.getDate().toString(),
      weekday: WEEKDAYS_JA[wd] ?? '',
      isWeekend: wd === 0 || wd === 6,
      isSunday: wd === 0,
      isToday: d.getTime() === today.getTime(),
      monthLabel: index === 0 || isFirstOfMonth ? `${(d.getMonth() + 1).toString()}月` : undefined,
    };
  });
}

/**
 * 候補プール用の週セクションを生成する。今週の月曜を week0 とし、POOL_WEEK_COUNT 週ぶん。
 * ラベルは各週月曜の "M/D"、`startDate` は同じ月曜の ISO 日付（保存の基準）。
 * @graph-connects none
 */
export function generatePoolWeeks(base: Date = new Date()): PoolWeek[] {
  const monday = atMidnight(base);
  const dow = monday.getDay();
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  monday.setDate(monday.getDate() + diffToMonday);

  return Array.from({ length: POOL_WEEK_COUNT }, (_, index) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + index * 7);
    return {
      index,
      label: `${(d.getMonth() + 1).toString()}/${d.getDate().toString()}`,
      startDate: toLocalIso(d),
    };
  });
}

/**
 * value を [min, max] にクランプする小さなヘルパー。
 * @graph-connects none
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
